import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { dashboardAPI } from '../services/api';
import { useDataCache } from '../hooks/useDataCache';
import { createCacheKey } from '../lib/cache';
import { cn } from '../lib/utils';
import ErrorBoundary from './ErrorBoundary';
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  Receipt,
  FileDown,
  CreditCard,
  UserPlus
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const navSections = [
  {
    title: 'OVERVIEW',
    items: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard, permission: null },
    ],
  },
  {
    title: 'MASTER DATA',
    items: [
      { path: '/banks', label: 'Banks', icon: Building2, permission: 'banks' },
      { path: '/agents', label: 'Agents', icon: Users, permission: 'agents' },
      { path: '/cms-directory', label: 'CMS Directory', icon: FileText, permission: 'banks' },
    ],
  },
  {
    title: 'OPERATIONS',
    items: [
      { path: '/billing', label: 'Billing', icon: Receipt, permission: 'invoices' },
      { path: '/payments', label: 'Payments', icon: CreditCard, permission: 'payments' },
      { path: '/reports', label: 'Reports', icon: BarChart3, permission: 'reports' },
    ],
  },
  {
    title: 'SYSTEM',
    items: [
      { path: '/settings', label: 'Settings', icon: Settings, permission: 'settings', adminOnly: true },
      { path: '/templates', label: 'Templates', icon: FileDown, permission: null },
      { path: '/staff', label: 'Staff', icon: UserPlus, permission: 'settings', adminOnly: true },
    ],
  },
];

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

export default function Layout({ children }) {
  const { user, logout, hasPermission } = useAuth();
  const { selectedMonth, setSelectedMonth, selectedYear, setSelectedYear, theme, toggleTheme, sidebarOpen, setSidebarOpen } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [badges, setBadges] = useState({});

  const dashboardKey = createCacheKey('/api/dashboard', { month: selectedMonth, year: selectedYear });
  const { data: dashboardData } = useDataCache(
    dashboardKey,
    () => dashboardAPI.get({ month: selectedMonth, year: selectedYear })
  );

  useEffect(() => {
    if (dashboardData) {
      const newBadges = {};
      if (dashboardData.certificates_submitted > 0) newBadges['/billing'] = dashboardData.certificates_submitted;
      if (dashboardData.payments_pending > 0) newBadges['/payments'] = '!';
      setBadges(newBadges);
    }
  }, [dashboardData]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isItemVisible = (item) => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    return true;
  };

  const visibleNavSections = navSections
    .map(section => ({ ...section, items: section.items.filter(isItemVisible) }))
    .filter(section => section.items.length > 0);

  const MONTH_YEAR_PATHS = ['/', '/billing', '/payments'];
  const showMonthYear = MONTH_YEAR_PATHS.includes(location.pathname);

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b z-50 flex items-center px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </Button>
        <div className="flex-1 flex items-center justify-center">
          <img src="/vajra-logo.jpg" alt="Vajra Group" className="h-8 w-8 rounded" />
          <span className="font-bold text-lg text-foreground ml-2">Vajra Group</span>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
        </Button>
      </header>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 bottom-0 w-64 bg-card border-r z-40 transform transition-transform duration-300 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          "lg:pt-0 pt-16"
        )}
      >
        {/* Logo */}
        <div className="hidden lg:flex h-16 items-center px-6 border-b border-border/50">
          <img src="/vajra-logo.jpg" alt="Vajra Group" className="w-10 h-10 rounded-lg mr-3 shadow-md" />
          <div>
            <h1 className="font-bold text-foreground">Vajra Group</h1>
            <p className="text-xs text-muted-foreground">Invoice Management System</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 overflow-y-auto h-[calc(100%-8rem)]">
          {visibleNavSections.map((section, sectionIdx) => (
            <div key={section.title} className={cn(sectionIdx === 0 ? "mt-0" : "mt-4")}>
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-2 px-3 select-none">
                {section.title}
              </div>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px]",
                        isActive
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:bg-primary/10 hover:text-foreground"
                      )
                    }
                    onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                  >
                    <item.icon size={20} />
                    <span className="flex-1">{item.label}</span>
                    {badges[item.path] && (
                      <span className="ml-auto bg-orange-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                        {badges[item.path]}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-card">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white font-medium">{user?.full_name?.charAt(0) || 'U'}</span>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role || 'staff'}</p>
                </div>
                <ChevronDown size={16} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/change-password')}>
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut size={16} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn("lg:ml-64 pt-16 lg:pt-0 min-h-screen transition-all duration-300")}>
        {/* Top Bar */}
        <header className="hidden lg:flex h-16 items-center justify-between px-6 border-b bg-card sticky top-0 z-30">
          <div className="flex items-center gap-4">
            {showMonthYear && (
              <>
                <Select value={(selectedMonth || (new Date().getMonth() + 1)).toString()} onValueChange={(v) => setSelectedMonth(parseInt(v, 10))}>
                  <SelectTrigger className="w-36" data-testid="month-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={(selectedYear || new Date().getFullYear()).toString()} onValueChange={(v) => setSelectedYear(parseInt(v, 10))}>
                  <SelectTrigger className="w-24" data-testid="year-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <div key={location.pathname} className="p-4 lg:p-6 pb-8 page-enter">
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </div>
        
        {/* Developer Credit */}
        <div className="text-center py-2 text-xs text-slate-400">
          App Designed & Developed by SIPL
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
